import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "@/hooks/useToast";
import memoryService, {
  MEMORY_CATEGORIES,
} from "@/services/memoryService";
import useAuthStore from "@/store/authStore";
import { Loader, Loader2, Search, Sparkles, Trash2, X } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { Checkbox, ScrollArea } from "../ui";

// Empty state component
function EmptyMemoriesState({ hasSearch, isWorkspaceContext }) {
  return (
    <div className="flex flex-col items-center justify-center w-full h-full min-h-[300px] rounded-lg border border-gray-300 dark:border-zinc-700 text-center p-8">
      <h4 className="text-[16px] font-semibold text-gray-900 dark:text-white mb-2">
        {hasSearch
          ? "No matching memories"
          : isWorkspaceContext
            ? "No project memories yet"
            : "No memories found"}
      </h4>
      <p className="text-[14px] text-gray-500 dark:text-gray-400 leading-relaxed max-w-md">
        {hasSearch
          ? "Try a different search term or clear the filters."
          : isWorkspaceContext
            ? "Memories are extracted automatically during project conversations."
            : "Memories are extracted automatically during conversations."}
      </p>
    </div>
  );
}

// Importance badge component
function ImportanceBadge({ importance }) {
  const colors = {
    high: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400",
    medium:
      "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400",
    low: "bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400",
  };

  if (!importance || importance === "medium") return null;

  return (
    <span
      className={`px-1.5 py-0.5 text-[10px] font-medium rounded ${colors[importance] || colors.medium}`}
    >
      {importance === "high" ? "!" : importance}
    </span>
  );
}

// Category pill component
function CategoryPill({ category }) {
  if (!category || category === "general" || category === "chat") return null;

  const label =
    MEMORY_CATEGORIES.find((c) => c.value === category)?.label || category;

  return (
    <span className="px-1.5 py-0.5 text-[10px] font-medium rounded bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400">
      {label}
    </span>
  );
}

// Source badge component
function SourceBadge({ source }) {
  if (!source || source === "manual") return null;

  if (source === "auto_analysis") {
    return (
      <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 text-[10px] font-medium rounded bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400">
        <Sparkles className="h-2.5 w-2.5" />
        auto
      </span>
    );
  }

  return null;
}

// Memory Card Component
function MemoryCard({
  memory,
  onDelete,
  isDeleting,
  selectMode,
  isSelected,
  onToggleSelect,
}) {
  const content = memory.memory || memory.content;
  const category = memory.category || memory.metadata?.category;
  const importance = memory.metadata?.importance;
  const source = memory.metadata?.source;
  const createdAt = memory.created_at || memory.metadata?.created_at;

  return (
    <div
      className={`group relative bg-gray-50 dark:bg-gray-900/50 border border-gray-300 dark:border-zinc-700/50 rounded-lg p-3 hover:border-gray-400 dark:hover:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-800/50 transition-all duration-200 w-full ${selectMode ? "pr-12" : ""}`}
    >
      {selectMode && (
        <div className="absolute top-3 right-3">
          <Checkbox
            checked={isSelected}
            onCheckedChange={onToggleSelect}
            className="data-[state=checked]:bg-blue-600 data-[state=checked]:border-blue-600 h-4 w-4 rounded"
          />
        </div>
      )}

      <div className="flex items-start gap-3 w-full min-w-0">
        <div className="flex-1 min-w-0">
          {/* Badges row */}
          <div className="flex items-center gap-1.5 mb-1.5 flex-wrap">
            <ImportanceBadge importance={importance} />
            <CategoryPill category={category} />
            <SourceBadge source={source} />
          </div>

          {/* Content */}
          <p className="text-[13px] text-gray-900 dark:text-white leading-relaxed">
            {content}
          </p>

          {/* Timestamp */}
          {createdAt && (
            <p className="text-[11px] text-gray-400 dark:text-gray-500 mt-1.5">
              {new Date(createdAt).toLocaleDateString()}
            </p>
          )}
        </div>

        {!selectMode && (
          <div className="flex-shrink-0">
            {isDeleting ? (
              <div className="h-6 w-6 rounded-full flex items-center justify-center">
                <Loader className="h-3 w-3 animate-spin text-red-500 dark:text-red-400" />
              </div>
            ) : (
              <Button
                variant="ghost"
                size="icon"
                onClick={() => onDelete(memory)}
                className="h-6 w-6 rounded-full p-0 flex items-center justify-center text-gray-400 hover:text-red-500 hover:bg-red-100 dark:hover:bg-red-900/20 transition-all duration-200"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// Constants
const ITEMS_PER_PAGE = 20;

// Memory system disabled for production release
const MEMORY_SYSTEM_ENABLED = false;

export function MemoryManagementDialog({ open, onOpenChange, workspaceId = null }) {
  // All hooks must be called before any conditional returns (React rules of hooks)
  const { user } = useAuthStore();
  const [memories, setMemories] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [deletingMemories, setDeletingMemories] = useState({});
  const [selectMode, setSelectMode] = useState(false);
  const [selectedMemories, setSelectedMemories] = useState([]);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [memoryToDelete, setMemoryToDelete] = useState(null);

  // Search and filter state
  const [searchQuery, setSearchQuery] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  const [categoryFilter, setCategoryFilter] = useState("all");

  // Pagination state
  const [hasMore, setHasMore] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [currentOffset, setCurrentOffset] = useState(0);

  // Load memories when dialog opens
  useEffect(() => {
    if (open && user?.id && MEMORY_SYSTEM_ENABLED) {
      loadMemories(true);
    }
  }, [open, user?.id]);

  // Reload when category filter changes (server-side filtering)
  useEffect(() => {
    if (open && user?.id && !searchQuery && MEMORY_SYSTEM_ENABLED) {
      loadMemories(true);
    }
  }, [categoryFilter]);

  // Reset state when dialog closes
  useEffect(() => {
    if (!open) {
      setSelectMode(false);
      setSelectedMemories([]);
      setSearchQuery("");
      setCategoryFilter("all");
      setCurrentOffset(0);
      setHasMore(false);
    }
  }, [open]);

  const loadMemories = async (reset = false) => {
    try {
      if (reset) {
        setIsLoading(true);
        setCurrentOffset(0);
      } else {
        setIsLoadingMore(true);
      }

      if (!user?.id) return;

      const offset = reset ? 0 : currentOffset;
      const result = await memoryService.getUserMemories(user.id, {
        category: categoryFilter !== "all" ? categoryFilter : undefined,
        workspace_id: workspaceId, // Filter by workspace when provided
        limit: ITEMS_PER_PAGE,
        offset,
        returnFullResponse: true,
      });

      if (reset) {
        setMemories(result.memories || []);
      } else {
        setMemories((prev) => [...prev, ...(result.memories || [])]);
      }

      setTotalCount(result.totalCount || 0);
      setHasMore(result.hasMore || false);
      setCurrentOffset(offset + ITEMS_PER_PAGE);
    } catch (err) {
      console.error("Error loading memories:", err);
      toast({ title: "Failed to load memories", variant: "destructive" });
    } finally {
      setIsLoading(false);
      setIsLoadingMore(false);
    }
  };

  const handleLoadMore = () => {
    if (hasMore && !isLoadingMore) {
      loadMemories(false);
    }
  };

  // Debounced search
  const handleSearch = useCallback(
    async (query) => {
      if (!query.trim()) {
        // Reset to server-side filtered list when search is cleared
        loadMemories(true);
        return;
      }

      setIsSearching(true);
      try {
        const results = await memoryService.searchMemories(user.id, query, {
          limit: 50,
          category: categoryFilter !== "all" ? categoryFilter : undefined,
          workspace_id: workspaceId, // Filter by workspace when provided
        });
        setMemories(results);
        setHasMore(false); // Search results don't support pagination
        setTotalCount(results.length);
      } catch (err) {
        console.error("Search failed:", err);
      } finally {
        setIsSearching(false);
      }
    },
    [user?.id, categoryFilter, workspaceId]
  );

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchQuery.trim()) {
        handleSearch(searchQuery);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery, handleSearch]);

  const handleDeleteMemory = async (memory) => {
    try {
      setDeletingMemories((prev) => ({ ...prev, [memory.id]: true }));

      const success = await memoryService.deleteMemory(memory.id, user.id);

      if (success) {
        setMemories((prev) => prev.filter((m) => m.id !== memory.id));
        setTotalCount((prev) => Math.max(0, prev - 1));
        toast({ title: "Memory deleted" });

        // Dispatch event to notify other components of memory count change
        window.dispatchEvent(
          new CustomEvent("memory-count-updated", {
            detail: { workspaceId, action: "delete" },
          })
        );
      } else {
        toast({ title: "Failed to delete memory", variant: "destructive" });
      }
    } catch (err) {
      console.error("Error deleting memory:", err);
      toast({ title: "Failed to delete memory", variant: "destructive" });
    } finally {
      setDeletingMemories((prev) => {
        const newState = { ...prev };
        delete newState[memory.id];
        return newState;
      });
      setMemoryToDelete(null);
      setShowDeleteConfirm(false);
    }
  };

  const handleDeleteSelectedMemories = async () => {
    if (selectedMemories.length === 0) return;

    try {
      setIsLoading(true);

      const result = await memoryService.deleteMemories(
        selectedMemories,
        user.id
      );

      if (result.success > 0) {
        setMemories((prev) =>
          prev.filter((m) => !selectedMemories.includes(m.id))
        );
        setTotalCount((prev) => Math.max(0, prev - result.success));
        setSelectedMemories([]);
        setSelectMode(false);
        toast({
          title: `${result.success} ${result.success === 1 ? "memory" : "memories"} deleted`,
        });

        // Dispatch event to notify other components of memory count change
        window.dispatchEvent(
          new CustomEvent("memory-count-updated", {
            detail: { workspaceId, action: "delete", count: result.success },
          })
        );
      }

      if (result.failed > 0) {
        toast({
          title: `Failed to delete ${result.failed} memories`,
          variant: "destructive",
        });
      }
    } catch (err) {
      console.error("Error deleting memories:", err);
      toast({ title: "Failed to delete memories", variant: "destructive" });
    } finally {
      setIsLoading(false);
      setShowDeleteConfirm(false);
    }
  };

  const handleToggleMemorySelection = (memoryId) => {
    setSelectedMemories((prev) =>
      prev.includes(memoryId)
        ? prev.filter((id) => id !== memoryId)
        : [...prev, memoryId]
    );
  };

  const confirmDelete = (memory) => {
    setMemoryToDelete(memory);
    setShowDeleteConfirm(true);
  };

  const clearSearch = () => {
    setSearchQuery("");
  };

  // Memory system disabled for production - return null after all hooks are called
  if (!MEMORY_SYSTEM_ENABLED) {
    return null;
  }

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-2xl bg-white dark:bg-zinc-900 border border-gray-200 dark:border-zinc-700/50 rounded-2xl">
          <DialogHeader className="pb-4">
            <DialogTitle className="text-[18px] font-semibold text-gray-900 dark:text-white">
              {workspaceId ? "Project memories" : "Saved memories"}
            </DialogTitle>
            <DialogDescription className="text-[14px] text-gray-600 dark:text-gray-400 leading-relaxed">
              {workspaceId ? (
                "Memories specific to this project. These are used when chatting within this project."
              ) : (
                <>
                  Memories help the AI personalize responses. They are stored
                  securely and never forgotten.{" "}
                  <a
                    href="https://docs.artifactchat.ai/features/memory"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-500 hover:text-blue-600 dark:text-blue-400 dark:hover:text-blue-300"
                  >
                    Learn more
                  </a>
                </>
              )}
            </DialogDescription>
          </DialogHeader>

          {/* Search and filter bar */}
          <div className="flex gap-2 pb-3">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search memories..."
                className="pl-9 pr-8 h-9 text-[13px] rounded-lg border-gray-300 dark:border-zinc-700 bg-white dark:bg-zinc-800"
              />
              {searchQuery && (
                <button
                  onClick={clearSearch}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
              {isSearching && (
                <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-gray-400" />
              )}
            </div>

            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
              <SelectTrigger className="w-[140px] h-9 text-[13px] rounded-lg border-gray-300 dark:border-zinc-700 bg-white dark:bg-zinc-800">
                <SelectValue placeholder="Category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all" className="text-[13px]">
                  All categories
                </SelectItem>
                {MEMORY_CATEGORIES.map((cat) => (
                  <SelectItem
                    key={cat.value}
                    value={cat.value}
                    className="text-[13px]"
                  >
                    {cat.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Action buttons - only show when memories exist */}
          {memories.length > 0 && (
            <div className="flex justify-end gap-2 pb-2">
              {selectMode ? (
                <>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setSelectMode(false);
                      setSelectedMemories([]);
                    }}
                    className="h-8 px-3 text-[13px] font-medium rounded-2xl bg-white dark:bg-transparent border border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white hover:bg-gray-100 dark:hover:bg-gray-800"
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowDeleteConfirm(true)}
                    disabled={selectedMemories.length === 0}
                    className="h-8 px-3 text-[13px] font-medium rounded-2xl bg-white dark:bg-transparent border border-red-500 dark:border-red-600 text-red-500 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 disabled:opacity-50"
                  >
                    Delete ({selectedMemories.length})
                  </Button>
                </>
              ) : (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setSelectMode(true)}
                  className="h-8 px-3 text-[13px] font-medium rounded-2xl bg-white dark:bg-transparent border border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white hover:bg-gray-100 dark:hover:bg-gray-800"
                >
                  Select
                </Button>
              )}
            </div>
          )}

          {/* Memory list */}
          <div className="min-h-[300px] max-h-[450px]">
            {isLoading ? (
              <div className="flex justify-center items-center h-[300px]">
                <Loader className="h-6 w-6 animate-spin text-blue-400" />
              </div>
            ) : memories.length > 0 ? (
              <ScrollArea className="h-[400px] pr-2">
                <div className="space-y-2">
                  {memories.map((memory) => (
                    <MemoryCard
                      key={memory.id}
                      memory={memory}
                      onDelete={confirmDelete}
                      isDeleting={deletingMemories[memory.id]}
                      selectMode={selectMode}
                      isSelected={selectedMemories.includes(memory.id)}
                      onToggleSelect={() =>
                        handleToggleMemorySelection(memory.id)
                      }
                    />
                  ))}

                  {/* Load More button */}
                  {hasMore && !searchQuery && (
                    <div className="flex justify-center pt-2 pb-1">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleLoadMore}
                        disabled={isLoadingMore}
                        className="h-8 px-4 text-[13px] font-medium rounded-2xl bg-white dark:bg-transparent border border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white hover:bg-gray-100 dark:hover:bg-gray-800"
                      >
                        {isLoadingMore ? (
                          <>
                            <Loader2 className="mr-2 h-3 w-3 animate-spin" />
                            Loading...
                          </>
                        ) : (
                          `Load more (${memories.length} of ${totalCount})`
                        )}
                      </Button>
                    </div>
                  )}
                </div>
              </ScrollArea>
            ) : (
              <EmptyMemoriesState
                hasSearch={!!searchQuery || categoryFilter !== "all"}
                isWorkspaceContext={!!workspaceId}
              />
            )}
          </div>

          {/* Footer with memory count */}
          <div className="flex justify-center items-center pt-4 border-t border-gray-200 dark:border-zinc-700/50">
            <span className="text-[12px] text-gray-400">
              {searchQuery
                ? `${memories.length} result${memories.length === 1 ? "" : "s"}`
                : `${totalCount} ${totalCount === 1 ? "memory" : "memories"}`}
              {categoryFilter !== "all" && ` in ${MEMORY_CATEGORIES.find(c => c.value === categoryFilter)?.label || categoryFilter}`}
            </span>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation dialog */}
      <Dialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <DialogContent className="max-w-md bg-white dark:bg-zinc-800 border border-gray-300 dark:border-zinc-700/50 rounded-2xl text-gray-900 dark:text-white">
          <DialogHeader className="space-y-3">
            <DialogTitle className="text-[16px] font-semibold text-gray-900 dark:text-white">
              Delete {memoryToDelete ? "memory" : "selected memories"}?
            </DialogTitle>
            <DialogDescription className="text-[13px] text-gray-600 dark:text-gray-400 leading-relaxed">
              {memoryToDelete
                ? "This memory will be permanently deleted. This action cannot be undone."
                : `${selectedMemories.length} memories will be permanently deleted. This action cannot be undone.`}
            </DialogDescription>
          </DialogHeader>
          <div className="flex gap-3 pt-4">
            <Button
              variant="outline"
              onClick={() => {
                setShowDeleteConfirm(false);
                setMemoryToDelete(null);
              }}
              className="flex-1 h-8 text-[13px] font-medium rounded-2xl bg-white dark:bg-transparent border border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white hover:bg-gray-100 dark:hover:bg-gray-800"
            >
              Cancel
            </Button>
            <Button
              onClick={async () => {
                if (memoryToDelete) {
                  await handleDeleteMemory(memoryToDelete);
                } else {
                  await handleDeleteSelectedMemories();
                }
              }}
              className="flex-1 h-8 bg-red-600 hover:bg-red-700 text-white text-[13px] font-medium rounded-2xl border-none"
            >
              Delete
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
